/**
 * "Thêm khách hàng": a customer is an account plus its primary subject (the account holder;
 * ADR-022). Sections: Tài khoản (login e-mail, phone, language, tier, currency), Chủ
 * thể chính — the SAME fields, checks and body as "Thêm chủ thể" (../subjects/SubjectFields.tsx,
 * ../../lib/subjectDraft.ts), Xuất hoá đơn ("giống chủ thể chính" by default) and Tài khoản
 * đăng nhập (the OWNER sign-in user, first password shown once). The account and its primary
 * subject (unverified; an organisation's legal representative created or linked with it) are
 * created in one request with one Idempotency-Key. Afterwards: what the subject still lacks to
 * register domains (normally nothing: the form asks for all of it).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { api, isApiError } from '../../api';
import type { CustomerCreated, NewCustomer, SubjectFieldError } from '../../api/areas/accounts';
import type { VnWard } from '../../api/types';
import { Callout } from '../../components/blocks';
import { useWrite, WriteError } from '../../components/Write';
import { blankAccount, checkAccount, invoicePreview, newCustomerBody, type AccountDraft, type FieldProblem } from '../../lib/customerProfile';
import { blankSubject, isHousehold, orgTaxCode, personal, subjectDisplayName, subjectErrors, subjectType, unprefixed, type SubjectDraft } from '../../lib/subjectDraft';
import { useApi } from '../../lib/useApi';
import { SecretOnce } from '../operator/shared';
import { ReadinessPanel } from '../subjects/ReadinessPanel';
import { SubjectFields } from '../subjects/SubjectFields';
import { useSubjectErrorText } from '../subjects/SubjectForm';
import { AccountFields, F, Group, InvoiceFields, selectClass } from './CustomerProfileFields';
import { paths, useTierLabel } from './shared';

type Lookup = (key: string, o?: Record<string, unknown>) => string;

function serverProblems(error: unknown): FieldProblem[] {
  if (!isApiError(error) || error.code !== 'VALIDATION_FAILED') return [];
  const fields = (error.details as { fields?: FieldProblem[] } | null)?.fields;
  return Array.isArray(fields) ? fields : [];
}

function Created({ r, onOpen }: { r: CustomerCreated; onOpen: () => void }) {
  const { t } = useTranslation('accounts');
  const tr = t as unknown as Lookup;
  return (
    <div className="grid gap-3" data-customer-created>
      <Callout tone="ok">{t('newCustomer.created')}</Callout>
      {r.warnings.length ? (
        <Callout tone="warn" title={t('newCustomer.duplicatesTitle')}>
          <ul className="list-disc pl-4">
            {r.warnings.map((w) => (
              <li key={`${w.field}-${w.customer_id}`}>
                {tr(`newCustomer.duplicateField.${w.field}`)}:{' '}
                <Link className="underline" to={paths.customer(w.customer_id)}>
                  {w.display_name}
                </Link>
              </li>
            ))}
          </ul>
        </Callout>
      ) : null}
      {r.login ? (
        r.login.initial_password ? (
          <>
            <Callout tone="warn">{t('customerActions.initialPasswordHint')}</Callout>
            <SecretOnce label={t('customerActions.initialPassword')} value={r.login.initial_password} />
          </>
        ) : r.login.secret_already_shown ? (
          <Callout tone="warn">{t('customerActions.alreadyShown')}</Callout>
        ) : (
          <Callout tone="info">{t('newCustomer.loginLinked')}</Callout>
        )
      ) : null}
      <Callout tone="info" title={t('newCustomer.subjectCreated')}>
        {t('newCustomer.subjectVerify')}{' '}
        <Link className="font-medium underline" to={paths.subject(r.subject.id)}>
          {t('newCustomer.openSubject')}
        </Link>
        {r.subject.representative ? (
          <>
            {' · '}
            <Link className="font-medium underline" to={paths.subject(r.subject.representative.id)}>
              {t('newCustomer.openRepresentative')}
            </Link>
          </>
        ) : null}
      </Callout>
      <ReadinessPanel subjectId={r.subject.id} compact />
      <DialogFooter>
        <Button type="button" onClick={onOpen}>
          {t('newCustomer.openCustomer')}
        </Button>
      </DialogFooter>
    </div>
  );
}

/** The one-line address the invoice would print (Vietnam: with the list's names). */
function useAddressText(d: SubjectDraft): string | null {
  const provinces = useApi((signal) => api.listVnProvinces({ signal }), []);
  const pid = Number(d.provinceId) || 0;
  const wards = useApi((signal) => (pid ? api.listVnWards(pid, { signal }) : Promise.resolve({ items: [] as VnWard[] })), [pid]);
  if (d.country.toUpperCase() === 'VN') {
    const p = provinces.status === 'success' ? provinces.data.items.find((x) => String(x.id) === d.provinceId) : undefined;
    const w = wards.status === 'success' ? wards.data.items.find((x) => String(x.id) === d.wardId) : undefined;
    const parts = [d.street.trim(), w?.name, p?.full_name].filter(Boolean);
    return parts.length ? `${parts.join(', ')}, Việt Nam` : null;
  }
  const parts = [d.street, d.city, d.state, d.postalCode].map((x) => x.trim()).filter(Boolean);
  return parts.length ? [...parts, d.country].join(', ') : null;
}

function Body({ onDone, onCancel }: { onDone: (id: string) => void; onCancel: () => void }) {
  const { t } = useTranslation('accounts');
  const tr = t as unknown as Lookup;
  const tiers = useApi((signal) => api.listCustomerTiers({ signal }), []);
  const tierLabel = useTierLabel();
  const [a, setA] = useState<AccountDraft>(blankAccount());
  const [s, setS] = useState<SubjectDraft>(blankSubject('INDIVIDUAL'));
  const [tier, setTier] = useState('STANDARD');
  const [currency, setCurrency] = useState('VND');
  const [createLogin, setCreateLogin] = useState(true);
  const [local, setLocal] = useState<FieldProblem[]>([]);
  const setAccount = (patch: Partial<AccountDraft>) => setA((x) => ({ ...x, ...patch }));
  const setSubject = (patch: Partial<SubjectDraft>) => setS((x) => ({ ...x, ...patch }));
  const w = useWrite((b: NewCustomer, opts) => api.createCustomer(b, opts));
  const problems = [...local, ...(w.state.phase === 'error' ? serverProblems(w.state.error) : [])];
  const subjectErr = useSubjectErrorText(unprefixed(problems as SubjectFieldError[], 'subject.'));
  const address = useAddressText(s);
  const result = w.state.phase === 'done' ? w.state.result : null;
  if (result) return <Created r={result} onOpen={() => onDone(result.id)} />;
  const tierItems: [string, string][] = tiers.status === 'success' ? tiers.data.items.map((x) => [x.code, tierLabel(x)]) : [['STANDARD', 'STANDARD']];
  const person = personal(s.kind, s.orgKind);
  const preview = invoicePreview(
    {
      name: subjectDisplayName(s),
      subjectType: subjectType(s),
      taxCode: isHousehold(s) ? s.householdTaxCode || s.idValue : person ? (s.idType === 'NATIONAL_ID' ? s.idValue : null) : orgTaxCode(s),
      address,
    },
    a.email || s.email,
  );

  const submit = () => {
    const filled = newCustomerBody(a, s, { tier, currency, createLogin });
    const found: FieldProblem[] = [
      ...checkAccount(filled.account, subjectType(filled.subject), filled.subject.country),
      ...subjectErrors(filled.subject).map((e) => ({ field: `subject.${e.field}`, code: e.code })),
    ];
    setLocal(found);
    if (found.length) return;
    void w.run(filled.body);
  };

  return (
    <form
      className="grid gap-4"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <AccountFields d={a} set={setAccount} problems={problems} country={s.country} taxCode={person ? '' : orgTaxCode(s)}>
        <F label={t('customerForm.tier')} error={problems.some((p) => p.field === 'tier') ? tr('customerForm.errors.TIER_UNKNOWN') : null}>
          <select className={selectClass} value={tier} onChange={(e) => setTier(e.target.value)}>
            {tierItems.map(([code, label]) => (
              <option key={code} value={code}>
                {label}
              </option>
            ))}
          </select>
        </F>
        <F label={t('newCustomer.currency')}>
          <select className={selectClass} value={currency} onChange={(e) => setCurrency(e.target.value)}>
            <option value="VND">VND</option>
            <option value="USD">USD</option>
          </select>
        </F>
      </AccountFields>

      <Group title={t('newCustomer.primarySubject')} className="sm:grid-cols-1">
        <p className="text-xs text-muted-foreground">{t('newCustomer.primarySubjectHint')}</p>
        <SubjectFields d={s} set={setSubject} err={subjectErr} />
      </Group>

      <InvoiceFields d={a} set={setAccount} problems={problems} preview={preview} personalTaxCode={person} />

      <Group title={t('customerForm.section.login')} className="sm:grid-cols-1">
        <label className="flex items-start gap-2 text-sm">
          <Checkbox className="mt-0.5" checked={createLogin} onCheckedChange={(v) => setCreateLogin(v === true)} />
          <span>
            {t('newCustomer.createLogin')}
            <span className="block text-xs text-muted-foreground">{t('newCustomer.createLoginHint', { email: (a.email || s.email).trim() || '…' })}</span>
          </span>
        </label>
      </Group>

      {w.state.phase === 'error' && serverProblems(w.state.error).length === 0 ? <WriteError error={w.state.error} /> : null}
      {problems.length ? <p className="text-sm text-critical">{t('customerForm.fixFields', { count: problems.length })}</p> : null}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          {t('newCustomer.cancel')}
        </Button>
        <Button type="submit" disabled={w.busy}>
          {t('newCustomer.create')}
        </Button>
      </DialogFooter>
    </form>
  );
}

export function NewCustomerDialog({ open, onOpenChange, onCreated }: { open: boolean; onOpenChange: (o: boolean) => void; onCreated: (id: string) => void }) {
  const { t } = useTranslation('accounts');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-3xl [&>*]:min-w-0" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>{t('newCustomer.title')}</DialogTitle>
          <DialogDescription>{t('newCustomer.description')}</DialogDescription>
        </DialogHeader>
        {open ? (
          <Body
            onDone={(id) => {
              onOpenChange(false);
              onCreated(id);
            }}
            onCancel={() => onOpenChange(false)}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
